import { Modal as ModalModule, UI, Utils } from 'common';
import React, { useContext, useEffect, useRef, useState } from 'react';
import csvPath from '../../assets/csv/new-location-request-form.csv';
import AccountCommunication from '../utils/AccountCommunication';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { WrappedFileUpload, WrappedInputTextarea } = UI;
const { Session, Util } = Utils;

const generateDigits = () =>
	(Math.floor(Math.random() * 10000) + 10000).toString().substring(1);

const RequestNewLocationsModal = ({ modalId }) => {
	const [csvFile, setCsvFile] = useState(null);
	const [fileName, setFileName] = useState('');
	const [specialInstructions, setSpecialInstructions] = useState('');
	const [referenceId, setReferenceId] = useState('');
	const [showSuccess, setShowSuccess] = useState(false);
	const fileUploader = useRef(null);
	const { showError } = useContext(ModalContext);

	const modalHeader = showSuccess
		? 'New Location Request Submitted'
		: 'Request New Locations';
	const dialog1 =
		'Your request to add locations to your Stamps.com Enterprise Account has been received.';
	const dialog2 =
		'The point of contact at each requested location will receive an email with login information and instructions to get started, typically within 5-7 business days.';
	const dialog3 =
		'We will contact you if any further information is required. For any questions, please contact enterprisesupport@stamps.com.';

	const fileReader = new FileReader();

	useEffect(() => {
		setReferenceId(`${generateDigits()}-${generateDigits()}`);
	}, []);

	// clear the file upload buffer and file name
	const clearUpload = () => {
		fileUploader.current.clear();
		setFileName('');
	};

	const checkCsvHeader = file => {
		fileReader.onload = event => {
			const csv = event.target.result;
			const headerRow = csv.split(/\r\n|\n/)[0];

			// use regex to split header, since some headers contain commas
			const headerColumns = headerRow.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g);
			const expectedHeaders = [
				'LOCATION TYPE',
				'"LOCATION NAME (i.e., Cost Center #, branch #, office name etc.) (29 character limit)"',
				'PRIMARY USER PHONE #',
				'PRIMARY USER FIRST NAME',
				'PRIMARY USER LAST NAME',
				'PRIMARY USER EMAIL ADDRESS (40 character limit)',
				'PRIMARY USER STREET ADDRESS (No PO Box) *Note - Address provided must be a USPS verified address*',
				'PRIMARY USER CITY',
				'PRIMARY USER STATE',
				'PRIMARY USER ZIP',
				'CUSTOM USERNAME (14 character limit) (OPTIONAL)',
				'CUSTOM PASSWORD (OPTIONAL)',
				'CUSTOM FIELD (OPTIONAL)',
				'EXISTING METER LEASE EXPIRATION DATE MM/YY (INTERNAL USE ONLY)',
				'SERVICE PLAN (INTERNAL USE ONLY)',
			];

			for (let i = 0; i < expectedHeaders.length - 1; i++) {
				if (headerColumns[i] !== expectedHeaders[i]) {
					clearUpload();

					return showError({
						data: {
							title: 'Invalid CSV file',
							description:
								'There was a problem with the selected file. Please verify your CSV file includes the original header row and try again.',
							handleType: 'custom',
						},
					});
				}
			}

			setCsvFile(csv);
		};

		// handle load error
		fileReader.onerror = event => {
			if (event.target.error.name === 'NotReadableError') {
				clearUpload();

				showError({
					data: {
						title: 'Import Error',
						description:
							'There was a problem importing your requested locations. Please verify your CSV and try again.',
						handleType: 'custom',
					},
				});
			}
		};

		// read file into memory as UTF-8 to trigger the events above
		fileReader.readAsText(file);
	};

	const onSelectCsvFile = ({ files }) => {
		const csvFileName = files[0]?.name;
		const fileTypes = ['csv', 'txt'];
		const isFileTypeValid = Util.isFileType(csvFileName, fileTypes);

		if (isFileTypeValid) {
			checkCsvHeader(files[0]);
			setFileName(csvFileName);
		} else {
			clearUpload();

			showError({
				data: {
					title: 'Invalid file type',
					description: 'Please select a file with a .csv or .txt extension.',
					handleType: 'custom',
				},
			});
		}
	};

	const onChangeSpecialInstructions = ({ target: { value } }) => {
		setSpecialInstructions(value);
	};

	const sendEmails = async () => {
		const { MerchantType } = Session.getSettings();
		const { customerId: custId } = Session.getAccountInfo();
		const {
			customerDetails: {
				email,
				address: { name },
			},
		} = Session.getAccountInfo();

		const emailParamsSupport = {
			TemplateName: 'requestNewLocations',
			NvPairs: {
				merchant: MerchantType,
				merchantUrl: 'stamps.com',
				strEmailAddress: 'enterprisesupport@stamps.com',
				strFirstName: 'Enterprise Account Requests',
				subject: 'New Location Request Submitted',
				isInternal: true,
				custId,
				email,
				referenceId,
				specialInstructions,
				dialog1,
				dialog2,
				dialog3,
				content: csvFile,
				isStamps: true,
				fileName: `${fileName.split('.')[0]}`,
			},
		};
		const emailParamsCustomer = {
			...emailParamsSupport,
			NvPairs: {
				...emailParamsSupport.NvPairs,
				strEmailAddress: email,
				strFirstName: name,
				isInternal: false,
			},
		};

		const [supportResponse, customerResponse] = await Promise.all([
			AccountCommunication.sendEmail(emailParamsSupport),
			AccountCommunication.sendEmail(emailParamsCustomer),
		]);

		if (!AccountCommunication.isSuccessful(supportResponse)) {
			return showError({ supportResponse });
		}

		if (!AccountCommunication.isSuccessful(customerResponse)) {
			return showError({ customerResponse });
		}

		setShowSuccess(true);
	};

	const onSubmitRequest = () => {
		if (fileName === '') {
			return showError({
				data: {
					title: 'No File Attached',
					description:
						'Please click the Select CSV File button and attach a file before clicking Import.',
					handleType: 'custom',
				},
			});
		}

		sendEmails();
	};

	const renderSuccessMessage = () => (
		<>
			<div className="request-info">
				<p>
					<b>New location Request Reference ID:</b> {referenceId}
				</p>
				<p>
					<b>Special Instructions:</b> {specialInstructions || 'n/a'}
				</p>
			</div>
			<div className="success-message">
				<p>{dialog1}</p>
				<p>{dialog2}</p>
				<p>{dialog3}</p>
			</div>
		</>
	);

	return (
		<Modal
			modalId={modalId}
			className="account-locations request-new-locations-modal"
			header={modalHeader}
			primaryButton={
				showSuccess
					? { type: ModalButtonActionType.Ok, text: 'Ok' }
					: {
							type: ModalButtonActionType.Ok,
							onClick: onSubmitRequest,
							text: 'Submit Request',
						}
			}
			secondaryButton={
				showSuccess
					? null
					: {
							type: ModalButtonActionType.Cancel,
							text: 'Cancel',
						}
			}
			size="small"
		>
			{showSuccess ? (
				renderSuccessMessage()
			) : (
				<div className="body">
					<div className="p-grid">
						<div className="p-col-12">
							<p>
								Submit a request for additional locations using our standard CSV
								file format. The new locations will be subject to your agreed
								upon per-location service fee.
							</p>
						</div>
						<div className="p-col-12">
							<a href={csvPath} target="_blank" rel="noreferrer">
								Download Sample File
							</a>
						</div>
						<div className="p-col-12 upload-container">
							<WrappedFileUpload
								ref={fileUploader}
								name="requestNewLocations"
								className="transparent"
								chooseLabel="Select CSV File"
								accept=".txt, .csv"
								mode="basic"
								auto
								onSelect={onSelectCsvFile}
								customUpload
								uploadHandler={() => {
									if (fileUploader.current && fileName !== '') {
										clearUpload();
									}
								}}
							/>
							<p>{fileName}</p>
						</div>
						<div className="p-col-12">
							<p>
								Our Account Generation Team typically processes these requests
								within 5-7 business days. Please include any special
								instructions below:
							</p>
						</div>
						<div className="p-col-12">
							<WrappedInputTextarea
								rows={2}
								cols={31}
								maxLength={60}
								value={specialInstructions}
								onChange={onChangeSpecialInstructions}
								dataqa="request-new-locations-special-instructions"
							/>
						</div>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default RequestNewLocationsModal;
